import { useState, useEffect, useMemo } from "react";
import { Clock, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  open: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  overdue: "bg-red-500/15 text-red-400 border-red-500/20",
};

export default function AccountsPayable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/quickbooks/accounts-payable`, { headers });
        if (!res.ok) return;
        setData(await res.json());
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!data?.bills) return [];
    if (!searchTerm) return data.bills;
    const term = searchTerm.toLowerCase();
    return data.bills.filter(b =>
      b.vendor_name?.toLowerCase().includes(term) || b.id?.includes(term)
    );
  }, [data, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Clock className="w-10 h-10 text-white/20 mb-4" />
        <p className="text-white/40 text-sm">No data yet. Run a sync first.</p>
      </div>
    );
  }

  const kpis = data.kpis;

  return (
    <div className="space-y-6" data-testid="ap-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Open A/P", value: kpis.total_open_ap, color: "text-white" },
          { label: "Due in 7 Days", value: kpis.due_7_days, color: "text-amber-400" },
          { label: "Due in 30 Days", value: kpis.due_30_days, color: "text-orange-400" },
          { label: "Overdue A/P", value: kpis.overdue_ap, color: "text-red-400" },
        ].map((item, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">{item.label}</p>
            <p className={`text-2xl font-light tracking-tight ${item.color}`}>
              ${item.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input
          type="text"
          placeholder="Search vendors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="ap-search"
          className="w-full pl-9 pr-4 py-2 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
        />
      </div>

      {/* Bills Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ap-table">
            <thead>
              <tr className="text-white/30 text-[11px] uppercase tracking-wider bg-white/[0.02]">
                <th className="text-left px-4 py-3 font-medium">Vendor</th>
                <th className="text-left px-4 py-3 font-medium">Bill Date</th>
                <th className="text-left px-4 py-3 font-medium">Due Date</th>
                <th className="text-right px-4 py-3 font-medium">Amount</th>
                <th className="text-right px-4 py-3 font-medium">Balance</th>
                <th className="text-center px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-white/30 text-sm">
                    No open bills
                  </td>
                </tr>
              ) : (
                filtered.map((bill, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-white/80 font-medium">{bill.vendor_name}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{bill.txn_date}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{bill.due_date}</td>
                    <td className="px-4 py-3 text-right text-white/60">
                      ${bill.total_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-right text-white/80 font-medium">
                      ${bill.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border capitalize ${STATUS_STYLES[bill.status] || ""}`}>
                        {bill.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
